import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ContentCut as ContentCutIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import placeServiceProfessionalsService from '../../services/placeServiceProfessionalsService';
import { usePlacesData } from '../../contexts/PlacesDataContext';

/**
 * ProfessionalServicesDialog
 * 
 * Dialog para configurar quais serviços um profissional pode realizar em um local.
 * 
 * Features:
 * - Lista todos os serviços disponíveis no local
 * - Permite marcar/desmarcar quais o profissional pode realizar
 * - Salva/remove vínculos PlaceServiceProfessional
 */
export default function ProfessionalServicesDialog({ 
  open, 
  onClose, 
  placeProfessional,
  place,
  onUpdate 
}) {
  const { getService } = usePlacesData();
  const [services, setServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Carregar serviços disponíveis e os já vinculados ao profissional
  useEffect(() => {
    if (open && placeProfessional && place) {
      loadData();
    }
  }, [open, placeProfessional, place]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Os serviços disponíveis no local vêm da prop 'place'
      const placeServices = place.services || [];
      setServices(placeServices);

      // Os serviceIds já vinculados ao profissional vêm do placeProfessional
      const existingServiceIds = placeProfessional.serviceIds || [];
      setSelectedServices(new Set(existingServiceIds));
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError('Erro ao carregar serviços');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleService = (serviceId) => {
    setSelectedServices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(serviceId)) {
        newSet.delete(serviceId);
      } else {
        newSet.add(serviceId);
      }
      return newSet;
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Identificar quais serviços adicionar e quais remover
      const existingIds = new Set(placeProfessional.serviceIds || []);
      const toAdd = [...selectedServices].filter(id => !existingIds.has(id));
      const toRemove = [...existingIds].filter(id => !selectedServices.has(id));

      // Adicionar novos vínculos
      for (const serviceId of toAdd) {
        const placeService = services.find(s => s.serviceId === serviceId);
        if (placeService) {
          // Backend espera: { placeProfessionalId }
          // O placeServiceId vai na URL
          await placeServiceProfessionalsService.create(
            place.id, 
            placeService.id, // Este é o PlaceServiceId (ID da relação place+service)
            { placeProfessionalId: placeProfessional.id }
          );
        }
      }

      // Remover vínculos desmarcados
      for (const serviceId of toRemove) {
        const placeService = services.find(s => s.serviceId === serviceId);
        if (placeService) {
          // Buscar a lista de profissionais vinculados para encontrar o ID do vínculo
          const existingLinks = await placeServiceProfessionalsService.getByPlaceServiceId(
            place.id,
            placeService.id
          );
          
          // Encontrar o vínculo específico deste profissional
          const linkToDelete = existingLinks.find(
            link => link.placeProfessionalId === placeProfessional.id
          );
          
          if (linkToDelete) {
            await placeServiceProfessionalsService.delete(
              place.id,
              placeService.id,
              linkToDelete.id
            );
          }
        }
      }

      if (onUpdate) {
        await onUpdate();
      }
      
      onClose();
    } catch (err) {
      console.error('Erro ao salvar configurações:', err);
      setError('Erro ao salvar: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <ContentCutIcon color="primary" />
          <Box>
            <Typography variant="h6">
              Serviços do Profissional
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {placeProfessional?.professionalName || 'Profissional'}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : services.length === 0 ? (
          <Alert severity="info">
            Este local ainda não possui serviços cadastrados. Adicione serviços ao local primeiro.
          </Alert>
        ) : (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Selecione quais serviços este profissional pode realizar neste local.
            </Alert>

            <List disablePadding>
              {services.map((placeService) => {
                const serviceId = placeService.serviceId;
                const service = getService(serviceId);
                const isSelected = selectedServices.has(serviceId);
                
                return (
                  <ListItem 
                    key={placeService.id} 
                    dense 
                    disableGutters
                    sx={{ 
                      '&:hover': { bgcolor: 'action.hover' },
                      cursor: 'pointer'
                    }}
                    onClick={() => handleToggleService(serviceId)}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={isSelected}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={service?.name || 'Serviço'}
                      secondary={service?.category}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={saving || loading}
          startIcon={saving && <CircularProgress size={16} />}
        >
          {saving ? 'Salvando...' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ProfessionalServicesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  placeProfessional: PropTypes.object,
  place: PropTypes.object,
  onUpdate: PropTypes.func,
};
